import { defineStore, acceptHMRUpdate } from 'pinia'
import useWebStore from './web'
import pinia from '../plugins/pinia'
import axios from '@src/libs/axios'
import defaultTheme, {
    definePalette,
    deriveDarkTheme,
    type Theme,
} from '../helper/theme'
import useUserStore from './user'
import type { QVueGlobals } from 'quasar'
import { Dark } from 'quasar'
import quasarEnUs from 'quasar/lang/en-US.js'
import quasarPtBr from 'quasar/lang/pt-BR.js'
import quasarPt from 'quasar/lang/pt.js'
import quasarEs from 'quasar/lang/es.js'
import type { Resource } from '../types/resource'
import deepcopy from 'ts-deepcopy'
const quasarLangs = {
    en_US: quasarEnUs,
    pt_BR: quasarPtBr,
    'pt-BR': quasarPtBr,
    pt: quasarPt,
    es: quasarEs,
} as any

/** Variantes de layout disponiveis; espelha Modules\Base\Helpers\LayoutVariants. */
export const layoutVariants = [
    'shadcn',
    'default',
    'tailwind',
    'tailwind2',
    'focus',
] as const

export type LayoutVariant = (typeof layoutVariants)[number]

/** Variante usada quando a empresa nao escolheu nenhuma; espelha o backend. */
export const DEFAULT_VARIANT: LayoutVariant = 'shadcn'

export type ColorScheme = 'light' | 'dark' | 'system'

// TODO: reativar junto com o modo escuro
// const prefersDark = () =>
//     typeof window !== 'undefined' &&
//     typeof window.matchMedia === 'function' &&
//     window.matchMedia('(prefers-color-scheme: dark)').matches

export const useLayoutStore = defineStore('layout', {
    state: () => ({
        drawer: true,
        settingsDrawer: true,
        /** Estado efetivo do modo escuro; derivado de `colorScheme`. */
        dark: false,
        /** Preferencia do usuario: claro, escuro ou seguir o sistema operacional. */
        colorScheme: 'light' as ColorScheme,
        /** Layout escolhido pela empresa na customizacao do sistema. */
        variant: DEFAULT_VARIANT as LayoutVariant,
        mini: false,
        hasLogo: false,
        theme: deepcopy(defaultTheme),
        clientType: 'client',
        locale: (navigator.language || window.env.APP_LOCALE || 'en').replace(
            '_',
            '-'
        ),
        qtdDefaultRows: 20,

        business: {
            name: null as string | null,
        },
        ad_panel_url: null as string | null,
        ad_panel_label: null as string | null,

        /** Preferencia do usuario no sininho de notificacoes ("mostrar somente nao lidas"). */
        onlyUnreadNotifications: false,
    }),
    actions: {
        changeLocale(locale: string, quasar: QVueGlobals) {
            this.locale = locale
            axios.defaults.headers.common['locale'] = locale
            axios.defaults.headers.common['Accept-Language'] = locale
            document.querySelector('html')?.setAttribute('lang', locale)
            if (locale in quasarLangs) quasar.lang.set(quasarLangs[locale])
            else quasar.lang.set(quasarLangs['en_US'])
        },
        updateTheme() {
            const { get } = useWebStore(pinia)
            const userStore = useUserStore(pinia)

            return get<Resource<any>>({
                url: 'core/config/layout' + (userStore.isAdmin ? '/admin' : ''),
                locale: '[App@UpdateTheme]',
                silent: true,
            })
                .then((response: any) => {
                    let obj = {
                        ...defaultTheme.light,
                        ...response.data.data.theme.light,
                    }
                    let light = definePalette(obj)
                    this.hasLogo = response.data.data.exists_logo || false
                    this.variant =
                        response.data.data.layout_variant || DEFAULT_VARIANT
                    this.clientType = response.data.data.client_type
                    this.business.name = response.data.data.name
                    this.ad_panel_url = response.data.data.ad_panel_url
                    this.ad_panel_label = response.data.data.ad_panel_label

                    this.theme = {
                        light,
                        dark: deriveDarkTheme(light),
                    }
                    this.applyColorScheme()
                })
                .catch(() => {})
        },
        /**
         * Aplica a preferencia de tema atual: classe `dark` no html (Tailwind),
         * modo escuro do Quasar e a paleta correspondente nas variaveis CSS.
         */
        applyColorScheme() {
            /**
             * TODO: modo escuro desativado temporariamente. Enquanto o tema dark
             * nao estiver pronto, forcamos o modo claro e ignoramos qualquer
             * preferencia salva anteriormente pelo usuario.
             */
            this.colorScheme = 'light'
            const dark = false
            /**
             * O layout classico (`default`) nunca teve modo escuro; so as
             * variantes novas respeitam a preferencia do usuario.
             */
            // const dark =
            //     this.variant !== 'default' &&
            //     (this.colorScheme === 'system'
            //         ? prefersDark()
            //         : this.colorScheme === 'dark')
            this.dark = dark

            document.documentElement.classList.toggle('dark', dark)
            document.documentElement.style.colorScheme = dark ? 'dark' : 'light'

            if (Dark.isActive !== dark) {
                Dark.set(dark)
            }

            definePalette(dark ? this.theme.dark : this.theme.light, dark)
        },
        setColorScheme(scheme: ColorScheme) {
            this.colorScheme = scheme
            this.applyColorScheme()
        },
        toggleDarkMode() {
            this.setColorScheme(this.dark ? 'light' : 'dark')
        },
        /**
         * Liga o modo escuro ao sistema operacional enquanto o usuario estiver
         * na preferencia "system".
         */
        watchSystemColorScheme() {
            if (typeof window?.matchMedia !== 'function') {
                return
            }
            window
                .matchMedia('(prefers-color-scheme: dark)')
                .addEventListener('change', () => {
                    if (this.colorScheme === 'system') {
                        this.applyColorScheme()
                    }
                })
        },
    },
    persist: true,
})

export default useLayoutStore
